import axios from 'axios';
import Cookies from 'js-cookie';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useState } from 'react';

const API_URL = 'http://localhost:3001';

const getToken = () => {
  return Cookies.get('authToken');
};

const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 10000,
});

apiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const registerUser = (userData) => apiClient.post('/register', userData);
export const loginUser = async (credentials) => {
  try {
    const response = await apiClient.post('/api/auth/login', credentials);
    return response;
  } catch (error) {
    console.error('API Error logging in:', error.response || error);
    throw error;
  }
};
export const placeOrder = (orderData) => apiClient.post('/order', orderData);

export const fetchUserProfile = () => apiClient.get('/profile');

export const changePassword = ({ oldPassword, newPassword }) => {
  return apiClient.post('/change-password', { oldPassword, newPassword });
};

export async function fetchCartItems() {
  try {
    console.log(`Fetching cart items...`);
    return await axios.get(`${API_URL}/cart/items`, {
      headers: {
        'Authorization': `Bearer ${Cookies.get('authToken')}`
      }
    });
  } catch (error) {
    console.error('Error fetching cart items:', error);
    throw error;
  }
}

export async function removeProduct(productId) {
  try {
    const response = await apiClient.delete(`/products/${productId}`);
    return response;
  } catch (error) {
    console.error('Error removing product:', error);
    throw error;
  }
}

export async function addItemToCart(cartName, items) {
  try {
    const response = await axios.post(`${API_URL}/cart`, {
      cartName,
      items,
    }, {
      headers: {
        'Authorization': `Bearer ${Cookies.get('authToken')}`,
        'Content-Type': 'application/json',
      },
    });
    return response;
  } catch (error) {
    console.error('Error adding item to cart:', error);
    throw error;
  }
}

export const renameCart = async (cartId, newName) => {
  try {
    const response = await apiClient.patch(`/api/carts/${cartId}`, {
      cartName: newName
    });
    return response;
  } catch (error) {
    console.error('API Error renaming cart:', error.response || error);
    throw error;
  }
};

export const fetchUserCarts = async () => {
  try {
    const response = await apiClient.get('/api/user/carts');
    return response;
  } catch (error) {
    console.error('API Error fetching user carts:', error.response || error);
    throw error;
  }
};

export const createNewCart = async (cartData) => {
  try {
    console.log("API: Calling createNewCart with data:", cartData);
    const response = await apiClient.post('/api/carts', cartData);
    console.log("API: createNewCart response:", response);
    if (!response || !response.data) {
        console.warn("API: createNewCart response missing data property.");
    }
    return response;
  } catch (error) {
    console.error('API Error creating new cart:', error);
    if (error.response) {
      console.error("API Error Response Data:", error.response.data);
      console.error("API Error Response Status:", error.response.status);
    } else if (error.request) {
      console.error("API Error Request:", error.request);
    } else {
      console.error('API Error Message:', error.message);
    }
    throw error;
  }
};

export const removeCartItem = async (cartItemId) => {
  if (!cartItemId) {
    console.error("removeCartItem called with invalid cartItemId:", cartItemId);
    throw new Error("Invalid item ID provided.");
  }
  try {
    const response = await apiClient.delete(`/api/cart/items/${cartItemId}`);
    return response;
  } catch (error) {
    console.error(`API Error removing cart item ${cartItemId}:`, error.response || error);
    throw error;
  }
};

export async function updateCartItem(cartItemId, updatedInfo) {
  try {
    const response = await axios.put(`${API_URL}/cart/items/${cartItemId}`, updatedInfo, {
      headers: {
        'Authorization': `Bearer ${Cookies.get('authToken')}`,
        'Content-Type': 'application/json',
      },
    });
    return response;
  } catch (error) {
    console.error('Error updating cart item:', error);
    throw error;
  }
}

export const deleteCart = async (cartId) => {
  try {
    console.log('Attempting to delete cart:', cartId); 
    const response = await apiClient.delete(`/api/carts/${cartId}`);
    console.log('Delete cart response:', response); 
    return response;
  } catch (error) {
    console.error('Error deleting cart:', error.response || error);
    throw error;
  }
};

export const fetchCartById = async (cartId) => {
  try {
    const response = await apiClient.get(`/api/carts/${cartId}/items`);
    return response;
  } catch (error) {
    console.error('Error fetching cart items:', error);
    throw error;
  }
};

export const getUserShippingAddress = async () => {
  try {
    const response = await apiClient.get('/api/user/shipping-address');
    return response;
  } catch (error) {
    console.error('API Error fetching shipping address:', error.response || error);
    // Don't throw, allow component to handle potentially empty data
    return error.response || { data: {} };
  }
};

export const getUserPaymentMethodHint = async () => {
  try {
    const response = await apiClient.get('/api/user/payment-method-hint');
    return response;
  } catch (error) {
    console.error('API Error fetching payment hint:', error.response || error);
    // Don't throw, allow component to handle potentially empty data
    return error.response || { data: {} };
  }
};

export const processCheckout = async (checkoutData) => {
  const token = getToken();
  if (!token) {
    const noTokenError = new Error("Authentication token not found. Please log in again.");
    console.error('API Error processing checkout:', noTokenError.message);
    throw noTokenError;
  }
  try {
    const response = await apiClient.post('/api/checkout', checkoutData);
    return response;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Unknown checkout error';
    const errorCode = error.response?.status || 'N/A';
    console.error(`API Error processing checkout (Status: ${errorCode}):`, errorMessage, error.response?.data);
    throw new Error(errorMessage);
  }
};

export const getCartDetails = async (cartIds) => {
  try {
    if (Array.isArray(cartIds)) {
      const response = await fetchUserCarts();
      const allCarts = response.data;
      return {
        data: allCarts.filter(cart => cartIds.includes(cart.cart_id))
      };
    }
    
    const response = await axios.get(`${API_URL}/user/carts/${cartIds}`, {
      headers: {
        'Authorization': `Bearer ${getToken()}`
      }
    });
    return response;
  } catch (error) {
    console.error('Error fetching cart details:', error);
    throw error;
  }
};

export const recalculateCartTotals = async (cartIds) => {
  try {
    const response = await apiClient.post('/api/carts/recalculate', { cartIds });
    return response;
  } catch (error) {
    console.error('Error recalculating cart totals:', error);
    throw error;
  }
};

export const getOrderSummary = async (cartIds) => {
  try {
    const response = await apiClient.get('/api/order-summary', {
      params: { cartIds: cartIds.join(',') }
    });
    return response;
  } catch (error) {
    console.error('Error fetching order summary:', error);
    throw error;
  }
};

export const searchCarts = async (query) => {
  try {
    console.log("API: Calling searchCarts with query:", query);
    const response = await apiClient.get('/api/carts/search', {
      params: { query }
    });
    console.log("API: searchCarts response:", response);
    if (!response || !response.data) {
        console.warn("API: searchCarts response missing data property.");
    }
    return response;
  } catch (error) {
    console.error('API Error searching carts:', error);
    if (error.response) {
      console.error("API Error Response Data:", error.response.data);
      console.error("API Error Response Status:", error.response.status);
    } else if (error.request) {
      console.error("API Error Request:", error.request);
    } else {
      console.error('API Error Message:', error.message);
    }
    throw error;
  }
};

export const moveCartItem = async (cartItemId, targetCartId) => {
  try {
    const response = await apiClient.patch(`/api/cart-items/${cartItemId}/move`, { targetCartId });
    return response;
  } catch (error) {
    console.error('API Error moving cart item:', error.response || error);
    throw error;
  }
};

export const updateCartItemQuantity = async (cartItemId, quantity) => {
  try {
    const response = await apiClient.patch(`/api/cart-items/${cartItemId}`, { quantity });
    return response;
  } catch (error) {
    console.error('API Error updating quantity:', error.response || error);
    throw error;
  }
};

export const fetchOrderHistory = async () => {
  try {
    const response = await apiClient.get('/api/orders');
    return response;
  } catch (error) {
    console.error('Error fetching order history:', error);
    throw error;
  }
};

export default apiClient;
